/**
 * Conversation API client — CRUD for chat history.
 */

import type { ConversationSummary, ConversationMessage, DeviceFingerprint } from '@skjv/common';
import { apiPath } from '@utils/api-base';

function headers(deviceId: string): Record<string, string> {
  return { 'Content-Type': 'application/json', 'X-Device-Id': deviceId };
}

/** List conversations for the current device */
export async function fetchConversations(deviceId: string): Promise<ConversationSummary[]> {
  const res = await fetch(apiPath('v1/conversations'), { headers: headers(deviceId) });
  if (!res.ok) throw new Error(`List conversations failed: ${res.status}`);
  return (await res.json()) as ConversationSummary[];
}

/** Create a new conversation */
export async function createConversation(
  deviceId: string,
): Promise<{ id: string; prunedId?: string }> {
  const res = await fetch(apiPath('v1/conversations'), {
    method: 'POST',
    headers: headers(deviceId),
  });
  if (!res.ok) throw new Error(`Create conversation failed: ${res.status}`);
  return (await res.json()) as { id: string; prunedId?: string };
}

/** Get all messages for a conversation */
export async function fetchConversationMessages(
  conversationId: string,
  deviceId: string,
): Promise<ConversationMessage[]> {
  const res = await fetch(apiPath(`v1/conversations/${conversationId}/messages`), {
    headers: headers(deviceId),
  });
  if (!res.ok) throw new Error(`Fetch messages failed: ${res.status}`);
  return (await res.json()) as ConversationMessage[];
}

/** Delete a conversation */
export async function deleteConversationApi(
  conversationId: string,
  deviceId: string,
): Promise<void> {
  const res = await fetch(apiPath(`v1/conversations/${conversationId}`), {
    method: 'DELETE',
    headers: headers(deviceId),
  });
  if (!res.ok) throw new Error(`Delete conversation failed: ${res.status}`);
}

/** Submit device fingerprint */
export async function submitFingerprint(fingerprint: DeviceFingerprint): Promise<void> {
  await fetch(apiPath('v1/devices/fingerprint'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fingerprint),
  });
}
